/**
 * ImageUploader — preview + Upload / Remove, shared by the profile photo and the
 * organization logo. Both are upload-only (no pasted-URL field): the file goes to
 * storage immediately through its own endpoint, so the preview is always what's saved.
 *
 * The picked file is handed to `onPick`; validation, resizing and the request are the
 * caller's, so this stays a dumb view. A broken <img> falls back to `fallback`.
 */
import { Avatar, Box, Button, CircularProgress, Typography } from '@mui/material';
import { Camera, Trash2 } from 'lucide-react';
import React from 'react';

export const IMAGE_ACCEPT = ['image/png', 'image/jpeg', 'image/webp'];

interface ImageUploaderProps {
  src?: string;
  /** Shown when there is no image (initials / building glyph). */
  fallback: React.ReactNode;
  shape: 'circle' | 'rounded';
  size?: number;
  /** 'uploading' | 'removing' while a request is in flight. */
  busy: 'uploading' | 'removing' | null;
  uploadLabel: string;
  hint: string;
  /** For screen readers: "profile photo" / "organization logo". */
  noun: string;
  onPick: (file: File) => void;
  onRemove: () => void;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  src,
  fallback,
  shape,
  size = 88,
  busy,
  uploadLabel,
  hint,
  noun,
  onPick,
  onRemove,
}) => {
  const disabled = busy !== null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Avatar
          {...(src ? { src } : {})}
          alt=""
          variant={shape === 'circle' ? 'circular' : 'rounded'}
          sx={{
            width: size,
            height: size,
            borderRadius: shape === 'circle' ? '50%' : '16px',
            bgcolor: src ? '#f1f5f9' : 'rgba(79,70,229,0.08)',
            color: '#4f46e5',
            fontSize: size * 0.34,
            fontWeight: 600,
            border: '1px solid',
            borderColor: 'divider',
            opacity: disabled ? 0.55 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {fallback}
        </Avatar>
        {disabled && (
          <CircularProgress
            size={26}
            sx={{ position: 'absolute', top: '50%', left: '50%', mt: '-13px', ml: '-13px' }}
          />
        )}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            component="label"
            variant="outlined"
            size="small"
            disabled={disabled}
            startIcon={<Camera size={16} strokeWidth={1.75} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'divider',
              color: 'text.primary',
            }}
          >
            {src ? 'Change' : uploadLabel}
            <input
              type="file"
              hidden
              aria-label={`Upload ${noun}`}
              accept={IMAGE_ACCEPT.join(',')}
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Reset so picking the SAME file again still fires onChange.
                e.target.value = '';
                if (file) onPick(file);
              }}
            />
          </Button>
          {src && (
            <Button
              variant="text"
              size="small"
              color="error"
              disabled={disabled}
              onClick={onRemove}
              aria-label={`Remove ${noun}`}
              startIcon={<Trash2 size={16} strokeWidth={1.75} />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Remove
            </Button>
          )}
        </Box>
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            mt: 1,
            maxWidth: 360,
            lineHeight: 1.5,
          }}
        >
          {hint}
        </Typography>
      </Box>
    </Box>
  );
};

export default ImageUploader;
